import { Slugs } from 'common/urls/Slugs';
import { chain, isEmpty, isNil, isNull, map } from 'lodash';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Link } from 'react-router-dom';
import {
  SubscriptionStatus,
  useActiveSubscriptionsQuery,
  useUpcomingGroupOrdersQuery,
} from 'src/gqlReactTypings.generated.d';
import { RecurringSubscription, TabNames } from 'src/pages/consumer/account/orders/utilities';
import { PrimaryButton } from 'src/shared/design-system/Buttons';
import { Loading } from 'src/shared/design-system/Loading';
import { Routes } from 'src/shared/Routes';
import { getSluggedPath } from 'src/shared/utils/RouteUtilities';
import { useUserPreferences } from 'src/user-preferences/useUserPreferences';
import { ConsumerOrdersCard } from './ConsumerOrdersCard';
import { EmptyOrdersContainer, OrdersListTitle } from './styles';

export const UpcomingOrdersTab = () => {
  const [{ zipcode: cachedZipCode }] = useUserPreferences();

  const { data, loading, fetchMore } = useUpcomingGroupOrdersQuery({ fetchPolicy: 'cache-and-network' });
  const { data: subsData, loading: subsLoading } = useActiveSubscriptionsQuery({ fetchPolicy: 'cache-and-network' });
  if (loading || !data || subsLoading || !subsData) {
    return <Loading />;
  }
  const { edges: subsEdges } = subsData.activeSubscriptions;

  // Only showing active subscriptions from recurring orders for now
  // since the UI relies on an actual order to render
  const activeSubscriptions = chain(subsEdges)
    .map('node')
    .filter(
      (s): s is RecurringSubscription =>
        !isNil(s.orderTemplate) && !isNil(s.recurringPeriodDays) && s.status === SubscriptionStatus.Active
    )
    .value();

  const { edges, pageInfo } = data.upcomingGroupOrders;
  if (isEmpty(edges)) {
    const path = isNull(cachedZipCode)
      ? Routes.CONSUMER_LANDING
      : getSluggedPath(Routes.CONSUMER_EXPLORE, [{ slug: Slugs.ZIP_CODE, value: cachedZipCode }]);

    return (
      <EmptyOrdersContainer>
        <div className='mb-20' />
        <OrdersListTitle>It's time to place an order!</OrdersListTitle>
        <Link to={path}>
          <PrimaryButton.M.RED>Explore shefs</PrimaryButton.M.RED>
        </Link>
      </EmptyOrdersContainer>
    );
  }

  const groupOrders = map(edges, 'node');
  return (
    <InfiniteScroll
      loader={<Loading />}
      dataLength={groupOrders.length}
      hasMore={pageInfo.hasNextPage}
      style={{ overflowY: 'hidden' }}
      next={() => fetchMore({ variables: { after: pageInfo.endCursor } }).catch(console.error)}>
      {groupOrders.map((groupOrder) => (
        <ConsumerOrdersCard
          groupOrder={groupOrder}
          key={groupOrder.id}
          activeTab={TabNames.UPCOMING}
          hasActiveSubscription={activeSubscriptions.length > 0}
        />
      ))}
    </InfiniteScroll>
  );
};
